/*
 * Соглашения каталога платформы viva, общие для страниц приложения.
 *
 * Аннотации кластеров пишет шаблон k8s-cluster (templates/k8s-cluster/skeleton/catalog),
 * аннотации моделей — шаблон ml-model; их же требует `platform-cli check-contract`
 * (viva/deploy) и читает плагин MLflow (mlbox/mlflow). Имена Application в ArgoCD
 * задаёт ApplicationSet кластера (viva/gitops): <модель>-staging и <модель>-production.
 */
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';

export const KUBERNETES_CLUSTER_TYPE = 'kubernetes-cluster';
export const ML_MODEL_TYPE = 'ml-model';
/** Сервисы классической разработки из шаблона app-service (Component в каталоге); свой тип,
 * чтобы не смешивать с платформенными Component'ами типа service. */
export const SERVICE_TYPE = 'app-service';
/** Репозитории определений признаков Feast (шаблон feature-repo, эталон teams/features). */
export const FEATURE_STORE_TYPE = 'feature-store';
/** JupyterHub кластера (Component jupyterhub-<кластер>, создаёт шаблон k8s-cluster). */
export const JUPYTERHUB_TYPE = 'jupyterhub';
/** Feast кластера (Component feast-<кластер>, создаёт шаблон k8s-cluster). */
export const FEAST_TYPE = 'feast';
/** Инстанс Airflow (Component airflow-<имя>, создаёт шаблон airflow; DAG'и — в его репозитории). */
export const AIRFLOW_TYPE = 'airflow';
export const AIRFLOW_URL_ANNOTATION = 'viva.company.com/airflow-url';
export const AIRFLOW_DAGS_REPO_ANNOTATION = 'viva.company.com/dags-repo';
/** DefectDojo кластера (Component defectdojo-<кластер>: компонент шаблона k8s-cluster или шаблон
 * defectdojo) — реестр уязвимостей, куда пайплайны шлют отчёты проверок безопасности. */
export const DEFECTDOJO_TYPE = 'defectdojo';
export const DEFECTDOJO_URL_ANNOTATION = 'viva.company.com/defectdojo-url';
/** Аннотация кластера: чей DefectDojo принимает отчёты его пайплайнов (имя кластера инстанса). */
export const CLUSTER_DEFECTDOJO_TARGET_ANNOTATION = 'viva.company.com/defectdojo-target';
/** RustFS кластера на хостах (Component rustfs-<кластер>, создаёт шаблон k8s-cluster для kubespray) —
 * объектное хранилище S3 платформы: Vault, MLflow, Loki, датасеты, бэкапы PostgreSQL. */
export const RUSTFS_TYPE = 'object-storage';
export const RUSTFS_URL_ANNOTATION = 'viva.company.com/rustfs-url';
export const RUSTFS_S3_URL_ANNOTATION = 'viva.company.com/s3-url';
export const RUSTFS_REPLICAS_ANNOTATION = 'viva.company.com/rustfs-replicas';
export const RUSTFS_POOL_ANNOTATION = 'viva.company.com/rustfs-pool';
/** S3 RustFS внутри кластера (сервис чарта) и бакеты платформы (platform-bootstrap.sh). */
export const RUSTFS_INTERNAL_S3 = 'http://rustfs-svc.rustfs.svc:9000';
export const rustfsBuckets = (cluster: string) => [
  { bucket: `${cluster}-vault`, usedBy: 'Vault — хранилище секретов (storage s3)' },
  { bucket: `${cluster}-mlflow`, usedBy: 'MLflow — артефакты экспериментов и модели' },
  { bucket: `${cluster}-datasets`, usedBy: 'датасеты DVC и Feast; бэкапы PostgreSQL (infra-db-backup/)' },
  { bucket: `${cluster}-loki`, usedBy: 'Loki — логи кластера' },
];
export const SERVICE_LANGUAGE_ANNOTATION = 'viva.company.com/language';
/** Окружения сервисов = namespace'ы кластера; feature-стенды — feat-<ветка>. */
export const SERVICE_ENVS = ['prod', 'stage', 'dev'] as const;
/** Хранилища данных из шаблонов <тип>-cluster (Resource в каталоге). */
export const BROKER_TYPE = 'kafka-cluster';
export const DATABASE_TYPE = 'postgres-cluster';
/** Хранилища на операторах MongoDB (Percona), RabbitMQ и Valkey — свои разделы меню. */
export const MONGODB_TYPE = 'mongodb-cluster';
export const RABBITMQ_TYPE = 'rabbitmq-cluster';
export const VALKEY_TYPE = 'valkey-cluster';
/**
 * Managed-хранилища облаков: сервис облака в сети кластера платформы, описание — юнит terragrunt в
 * viva/clusters (<облако>/<кластер>/datastores/<тип>-<имя>). cloud.ru Advanced — шаблоны «PostgreSQL /
 * MySQL / Redis / RabbitMQ (cloud.ru)», Resource managed-<тип>; VK Cloud — шаблоны «PostgreSQL / MySQL /
 * Redis (VK Cloud)», Resource vkcloud-<тип> (свои типы, чтобы страницы облаков не смешивались).
 */
export const MANAGED_DATASTORE_TYPES = [
  'managed-postgres',
  'managed-mysql',
  'managed-redis',
  'managed-rabbitmq',
  'vkcloud-postgres',
  'vkcloud-mysql',
  'vkcloud-redis',
];
/**
 * cloud.ru Advanced подключён на стенде (ключ платформы CLOUDRU_ADVANCED_* в .env; конфиг
 * viva.cloudru.enabled — 'true'): без него раздел «Managed (cloud.ru)» скрыт, вариант cloud.ru в формах
 * и шаблоны managed-хранилищ убирает entrypoint образа.
 */
export const isCloudruEnabled = (config: { getOptional(key: string): unknown }): boolean =>
  String(config.getOptional('viva.cloudru.enabled') ?? '') === 'true';
/**
 * VK Cloud подключён на стенде (пользователь платформы VKCLOUD_* в .env; конфиг viva.vkcloud.enabled —
 * 'true'): без него раздел «VK Cloud» скрыт, вариант VK Cloud в формах кластера и шаблоны managed-баз
 * vkcloud-* убирает entrypoint образа.
 */
export const isVkcloudEnabled = (config: { getOptional(key: string): unknown }): boolean =>
  String(config.getOptional('viva.vkcloud.enabled') ?? '') === 'true';
/**
 * Площадки Proxmox VE подключены на стенде (PROXMOX_CLUSTERS в .env; конфиг viva.proxmox.enabled — 'true'): без
 * них раздел «Proxmox» скрыт, вариант «VM на площадке Proxmox VE» формы кластера убирает entrypoint образа.
 */
export const isProxmoxEnabled = (config: { getOptional(key: string): unknown }): boolean =>
  String(config.getOptional('viva.proxmox.enabled') ?? '') === 'true';

/** Облако managed-хранилищ — значение target кластера и раздел меню. */
export type ManagedCloud = 'cloudru' | 'vkcloud';
export type ManagedCloudInfo = {
  /** короткое имя в заголовках: «PostgreSQL (cloud.ru)» */
  short: string;
  /** полное имя в подзаголовках и подсказках */
  title: string;
  /** признак подключения облака на стенде (конфиг viva.<облако>.enabled) */
  enabled: (config: { getOptional(key: string): unknown }) => boolean;
  /** учётные данные платформы в .env стенда — подсказка страницы «не подключён» */
  credentials: string;
};
export const MANAGED_CLOUDS: Record<ManagedCloud, ManagedCloudInfo> = {
  cloudru: {
    short: 'cloud.ru',
    title: 'cloud.ru Advanced',
    enabled: isCloudruEnabled,
    credentials: 'ключ платформы CLOUDRU_ADVANCED_*',
  },
  vkcloud: {
    short: 'VK Cloud',
    title: 'VK Cloud',
    enabled: isVkcloudEnabled,
    credentials: 'пользователь платформы VKCLOUD_*',
  },
};
/** Раздел меню облака: managed-сервис — пункт, своя страница и шаблон создания. */
export type ManagedService = {
  cloud: ManagedCloud;
  /** spec.type ресурса и шаблона создания */
  type: string;
  kind: string;
  title: string;
  /** сервис облака (RDS, DCS, DMS cloud.ru; Cloud Databases VK) */
  service: string;
  /** пункт меню */
  menu: string;
  path: string;
  template: string;
};
const cloudruService = (kind: string, title: string, service: string): ManagedService => ({
  cloud: 'cloudru',
  type: `managed-${kind}`,
  kind,
  title,
  service,
  menu: `${title} (${service})`,
  path: `/cloudru/${kind}`,
  template: `/create/templates/default/managed-${kind}`,
});
// у VK все базы — один сервис Cloud Databases: в пункте меню только технология
const vkcloudService = (kind: string, title: string): ManagedService => ({
  cloud: 'vkcloud',
  type: `vkcloud-${kind}`,
  kind,
  title,
  service: 'Cloud Databases',
  menu: title,
  path: `/vkcloud/${kind}`,
  template: `/create/templates/default/vkcloud-${kind}`,
});
export const CLOUDRU_SERVICES: ManagedService[] = [
  cloudruService('postgres', 'PostgreSQL', 'RDS'),
  cloudruService('mysql', 'MySQL', 'RDS'),
  cloudruService('redis', 'Redis', 'DCS'),
  cloudruService('rabbitmq', 'RabbitMQ', 'DMS'),
];
export const VKCLOUD_SERVICES: ManagedService[] = [
  vkcloudService('postgres', 'PostgreSQL'),
  vkcloudService('mysql', 'MySQL'),
  vkcloudService('redis', 'Redis'),
];
export const MANAGED_SERVICES: ManagedService[] = [...CLOUDRU_SERVICES, ...VKCLOUD_SERVICES];
/** Managed-сервис по spec.type ресурса (managed-<тип> / vkcloud-<тип>); не managed — undefined. */
export const managedServiceOf = (type: string): ManagedService | undefined =>
  MANAGED_SERVICES.find(s => s.type === type);
export const MANAGED_DATASTORE_LABELS: Record<string, string> = Object.fromEntries(
  MANAGED_SERVICES.map(s => [s.type, s.cloud === 'cloudru' ? s.menu : `${s.title} (${MANAGED_CLOUDS[s.cloud].short})`]),
);
export const DATASTORE_CLUSTER_ANNOTATION = 'viva.company.com/cluster';
/** Адрес хранилища внутри кластера: bootstrap Kafka или rw-сервис PostgreSQL. */
export const DATASTORE_ENDPOINT_ANNOTATIONS = [
  'viva.company.com/bootstrap',
  'viva.company.com/endpoint',
];

/** Кластера окружений сервиса (шаблон app-service); dev — DATASTORE_CLUSTER_ANNOTATION. */
export const SERVICE_STAGE_CLUSTER_ANNOTATION = 'viva.company.com/stage-cluster';
export const SERVICE_PROD_CLUSTER_ANNOTATION = 'viva.company.com/prod-cluster';
/** Кластера модели (шаблон ml-model); обучение — DATASTORE_CLUSTER_ANNOTATION, prod — через запятую. */
export const MODEL_STAGING_CLUSTER_ANNOTATION = 'viva.company.com/staging-cluster';
export const MODEL_PROD_CLUSTERS_ANNOTATION = 'viva.company.com/prod-clusters';

/** GitLab платформы: Resource gitlab, его публикует модуль каталога из integrations.gitlab. */
export const GITLAB_TYPE = 'gitlab';
export const GITLAB_URL_ANNOTATION = 'viva.company.com/gitlab-url';
/** Кластеры, собирающие метрики ресурса (у gitlab — с компонентом gitlab-metrics), через запятую. */
export const METRICS_CLUSTERS_ANNOTATION = 'viva.company.com/metrics-clusters';

export const CLUSTER_TARGET_ANNOTATION = 'viva.company.com/cluster-target';
export const CLUSTER_ENV_ANNOTATION = 'viva.company.com/cluster-env';
export const CLUSTER_DOMAIN_ANNOTATION = 'viva.company.com/cluster-domain';

export const MLFLOW_MODEL_NAME_ANNOTATION = 'mlflow.company.com/model-name';
export const MLFLOW_PRIMARY_METRIC_ANNOTATION =
  'mlflow.company.com/primary-metric';
export const MLFLOW_CHAMPION_ALIAS_ANNOTATION =
  'mlflow.company.com/champion-alias';
export const DEFAULT_CHAMPION_ALIAS = 'champion';

/** Окружения сервинга в порядке «важности»: production раньше staging. */
export const SERVING_ENVS = ['production', 'staging'] as const;
export type ServingEnv = (typeof SERVING_ENVS)[number];

/** Человеческие названия платформ кластера (input `target` шаблона k8s-cluster). */
export const CLUSTER_TARGET_LABELS: Record<string, string> = {
  yc: 'Yandex Cloud',
  cloudru: 'cloud.ru Advanced',
  vkcloud: 'VK Cloud',
  kubespray: 'Kubespray',
};

/** Ссылки кластера, которые показываем прямо в таблице (metadata.links[].title). */
export const CLUSTER_SERVICE_LINKS = [
  'ArgoCD',
  'Grafana',
  'MLflow',
  'Vault',
  'JupyterHub',
  'Feast',
];

export const routes = {
  home: '/',
  clusters: '/clusters',
  models: '/models',
  catalog: '/catalog',
  docs: '/docs',
  newCluster: '/create/templates/default/k8s-cluster',
  newModel: '/create/templates/default/ml-model',
  services: '/services',
  jupyterhub: '/jupyterhub',
  feast: '/feast',
  airflow: '/airflow',
  defectdojo: '/defectdojo',
  rustfs: '/rustfs',
  /** GitLab платформы — сразу страница ресурса (он один, его публикует модуль каталога). */
  gitlab: '/catalog/default/resource/gitlab',
  newAirflow: '/create/templates/default/airflow',
  newDefectDojo: '/create/templates/default/defectdojo',
  newService: '/create/templates/default/app-service',
  newFeatureRepo: '/create/templates/default/feature-repo',
  deleteService: '/create/templates/default/app-service-delete',
  databases: '/databases',
  brokers: '/brokers',
  newKafka: '/create/templates/default/kafka-cluster',
  newPostgres: '/create/templates/default/postgres-cluster',
  mongodb: '/mongodb',
  rabbitmq: '/rabbitmq',
  valkey: '/valkey',
  newMongodb: '/create/templates/default/mongodb-cluster',
  newRabbitmq: '/create/templates/default/rabbitmq-cluster',
  newValkey: '/create/templates/default/valkey-cluster',
  // прежний общий раздел managed-хранилищ — переадресация на раздел cloud.ru
  managedDatastores: '/managed-datastores',
  cloudru: '/cloudru/postgres',
  vkcloud: '/vkcloud/postgres',
  // площадки Proxmox VE (раздел «Proxmox»: вкладки площадок, VM, сетей, пулов, образов) и шаблоны его ресурсов
  proxmox: '/proxmox',
  newProxmoxVm: '/create/templates/default/proxmox-vm',
  newProxmoxPool: '/create/templates/default/proxmox-pool',
  newProxmoxImage: '/create/templates/default/proxmox-image',
  newProxmoxNetwork: '/create/templates/default/proxmox-network',
  deleteProxmoxResource: '/create/templates/default/proxmox-resource-delete',
} as const;

export const annotation = (entity: Entity, key: string): string | undefined =>
  entity.metadata.annotations?.[key];

/** Кластер платформы сущности и её окружения в нём (подпись в выпадающем списке «Метрик»). */
export type EntityCluster = { cluster: string; roles: string[] };

/**
 * Кластера платформы, в Prometheus которых есть метрики сущности, по порядку: основной (dev
 * сервиса, кластер обучения модели, кластер хранилища или инстанса), затем остальные окружения.
 * Кластер Kubernetes — он сам. Пусто — у сущности нет привязки к кластеру.
 */
export const entityClusters = (entity: Entity): EntityCluster[] => {
  const type = entity.spec?.type;
  if (entity.kind === 'Resource' && type === KUBERNETES_CLUSTER_TYPE) {
    return [{ cluster: entity.metadata.name, roles: [] }];
  }
  if (entity.kind === 'Resource' && type === GITLAB_TYPE) {
    return (annotation(entity, METRICS_CLUSTERS_ANNOTATION) ?? '')
      .split(',')
      .map(c => c.trim())
      .filter(Boolean)
      .map(cluster => ({ cluster, roles: [] }));
  }
  const main = annotation(entity, DATASTORE_CLUSTER_ANNOTATION);
  const pairs: [string | undefined, string | undefined][] = [];
  if (type === SERVICE_TYPE) {
    pairs.push(
      [main, 'dev'],
      [annotation(entity, SERVICE_STAGE_CLUSTER_ANNOTATION), 'stage'],
      [annotation(entity, SERVICE_PROD_CLUSTER_ANNOTATION), 'prod'],
    );
  } else if (type === ML_MODEL_TYPE) {
    pairs.push(
      [main, 'обучение'],
      [annotation(entity, MODEL_STAGING_CLUSTER_ANNOTATION), 'staging'],
    );
    for (const cluster of (
      annotation(entity, MODEL_PROD_CLUSTERS_ANNOTATION) ?? ''
    ).split(',')) {
      pairs.push([cluster, 'production']);
    }
  } else {
    pairs.push([main, undefined]);
  }
  const clusters: EntityCluster[] = [];
  for (const [raw, role] of pairs) {
    const cluster = raw?.trim();
    if (!cluster) continue;
    let item = clusters.find(c => c.cluster === cluster);
    if (!item) {
      item = { cluster, roles: [] };
      clusters.push(item);
    }
    if (role && !item.roles.includes(role)) item.roles.push(role);
  }
  return clusters;
};

/** Порядок окружений для сортировки: production, staging, затем всё остальное. */
export const envRank = (env?: string): number => {
  const index = SERVING_ENVS.indexOf(env as ServingEnv);
  return index === -1 ? SERVING_ENVS.length : index;
};

/** Окружение сервинга по имени Application: reference-model-staging -> staging. */
export const servingEnvOf = (
  appName: string,
  modelName: string,
): ServingEnv | undefined =>
  SERVING_ENVS.find(env => appName === `${modelName}-${env}`);

/** Окружение сервиса по имени Application: demo-go-dev -> dev, demo-go-feat-x -> feat-x. */
export const serviceEnvOf = (
  appName: string,
  serviceName: string,
): string | undefined =>
  appName.startsWith(`${serviceName}-`)
    ? appName.slice(serviceName.length + 1)
    : undefined;

/** prod, stage, dev, затем feature-стенды, затем прочее. */
export const serviceEnvRank = (env: string | undefined): number => {
  if (!env) return -1;
  const index = SERVICE_ENVS.indexOf(env as (typeof SERVICE_ENVS)[number]);
  if (index !== -1) return index;
  return env.startsWith('feat-')
    ? SERVICE_ENVS.length
    : SERVICE_ENVS.length + 1;
};

/** Имя хранилища без префикса типа: postgres-analytics -> analytics (аннотация или имя). */
export const DATASTORE_NAME_ANNOTATION = 'viva.company.com/datastore-name';
export const datastoreNameOf = (entity: Entity): string =>
  annotation(entity, DATASTORE_NAME_ANNOTATION) ??
  entity.metadata.name.replace(/^(postgres|kafka|mongodb|rabbitmq|valkey)-/, '');

export const compareByName = (a: Entity, b: Entity): number =>
  (a.metadata.title ?? a.metadata.name).localeCompare(
    b.metadata.title ?? b.metadata.name,
    'ru',
  );

/** Шаблон «Удалить сервис» с уже выбранным сервисом (formData scaffolder'а). */
export const deleteServiceUrl = (entity: Entity): string =>
  `${routes.deleteService}?formData=${encodeURIComponent(
    JSON.stringify({ service: stringifyEntityRef(entity) }),
  )}`;
